import type { OpUnitType } from "dayjs";
import dayjs from "dayjs";

import Sentry from "@similarweb/sentry-logger";

import { objectKeys } from "app/helpers/objects";
import translateNested from "app/pages/sales-intelligence/common-components/i18n/translateNested";
import type {
    IPermanentSignal,
    Signal,
    SignalFilters,
    SignalId,
    SignalSource,
    SignalType,
} from "app/pages/sales-intelligence/domain/signals/types/signal";
import { signalTypeMap } from "app/pages/sales-intelligence/domain/signals/types/signal";
import type {
    SignalsGroupedById,
    SignalsGroupedByType,
} from "app/pages/sales-intelligence/domain/signals/types/signal-collection";
import isDraftSignal from "app/pages/sales-intelligence/domain/signals/types/type-guards/isDraftSignal";
import isPermanentSignal from "app/pages/sales-intelligence/domain/signals/types/type-guards/isPermanentSignal";
import { getSignalIdsFromGroupedSignals } from "app/pages/sales-intelligence/domain/signals/utils/signal-ids";
import type { GroupedTechnologies } from "app/pages/sales-intelligence/domain/technology/types/model";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import { globalStringSanitizePipeline } from "app/pages/sales-intelligence/services/text-transform/stringTransformPipeline";
import type { Topic } from "app/pages/sales-intelligence/sub-modules/intent/types/common";
import type SignalLifecycleState from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalLifecycleState";
import type SignalsSearchFilters from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalsSearchFilters";

import IntentSignalTemplatesManager from "app/pages/sales-intelligence/sub-modules/signals/services/templates/intent/IntentSignalTemplatesManager";
import NewsSignalTemplatesManager from "app/pages/sales-intelligence/sub-modules/signals/services/templates/news/NewsSignalTemplatesManager";
import type { SignalsDigestParams, SignalsDigestType } from "app/pages/sales-intelligence/sub-modules/signals/types";
import { NEWS_CATEGORIES_BY_TYPE } from "app/pages/sales-intelligence/sub-modules/signals/utils/news/const";
import { is } from "app/pages/sales-intelligence/utils/type-guards";
import type { ICountryObject } from "app/services/CountryService";
import { granularityNameToMomentUnit } from "app/UtilitiesAndConstants/Constants/Moment";

import type CountPerSignalId from "../models/miscellaneous/CountPerSignalId";
import type PreAppliedSignalsPayload from "../models/miscellaneous/PreAppliedSignalsPayload";
import type SignalCreateDTO from "../models/miscellaneous/SignalCreateDTO";
import type { SignalsDescription } from "../models/miscellaneous/SignalsDescription";
import { SignalTimeframePeriod } from "../models/miscellaneous/SignalTimeframe";
import type SignalTypeIcons from "../models/miscellaneous/SignalTypeIcons";
import type SignalUpdateDTO from "../models/miscellaneous/SignalUpdateDTO";
import type TemplatesBySignalType from "../models/miscellaneous/TemplatesBySignalType";
import NewsSignalSubType from "../models/news/NewsSignalSubType";
import type { FiltersEqualityPredicate } from "../models/services/FilterService";
import type SignalsService from "../models/services/SignalsService";
import type SignalTemplate from "../models/templates/SignalTemplate";
import type SignalTemplatesManager from "../models/templates/SignalTemplatesManager";
import TrafficSignalSubType from "../models/traffic/TrafficSignalSubType";
import type UISignal from "../models/view/UISignal";
import type UISignalDataDeps from "../models/view/UISignalDataDeps";
import type UISignalsGroupedByType from "../models/view/UISignalsGroupedByType";

import AdNetworkSignalTemplatesManager from "./templates/ad-network/AdNetworkSignalTemplatesManager";
import ComingSoonSignalTemplatesManager from "./templates/coming-soon/ComingSoonSignalTemplatesManager";
import EcommerceSignalTemplatesManager from "./templates/ecommerce/EcommerceSignalTemplatesManager";
import TechnologySignalTemplatesManager from "./templates/technology/TechnologySignalTemplatesManager";
import TrafficSignalTemplatesManager from "./templates/traffic/TrafficSignalTemplatesManager";
import adNetworkUISignalFactory from "./ui-signals/ad-network/adNetworkUISignalFactory";
import ecommerceUISignalFactory from "./ui-signals/ecommerce/ecommerceUISignalFactory";
import intentUISignalFactory from "./ui-signals/intent/intentUISignalFactory";
import newsUISignalFactory from "./ui-signals/news/newsUISignalFactory";
import technologyUISignalFactory from "./ui-signals/technology/technologyUISignalFactory";
import trafficUISignalFactory from "./ui-signals/traffic/trafficUISignalFactory";
import type UISignalFactory from "./ui-signals/UISignalFactory";

type Deps = {
    settingsProvider: {
        isConsolidatedAppsEnabled: boolean;
    };
    settingsService: {
        getCurrentComponentCountries(): readonly ICountryObject[];
    };
    filterService: {
        getFiltersEqualityPredicate(signalType: SignalType): FiltersEqualityPredicate;
    };
};

export default function createSignalsService(deps: Deps): SignalsService {
    const { settingsService, settingsProvider, filterService } = deps;

    const validSignalTypes: readonly SignalType[] = [
        signalTypeMap.news,
        signalTypeMap.traffic,
        signalTypeMap.technology,
        signalTypeMap.adNetwork,
        signalTypeMap.ecommerce,
    ];

    function getSignalTypeLifecycleStates(): Readonly<Partial<Record<SignalType, SignalLifecycleState>>> {
        return {
            ...(settingsProvider.isConsolidatedAppsEnabled
                ? { [signalTypeMap.advertiserActivity]: "coming-soon", [signalTypeMap.apps]: "coming-soon" }
                : {}),
            ...(salesSettingsHelper.isInternalUser() ? { [signalTypeMap.roleChanges]: "coming-soon" } : {}),
        };
    }

    function getComingSoonSignalTypes(): readonly SignalType[] {
        const lifecycleStates = getSignalTypeLifecycleStates();

        return (Object.keys(lifecycleStates) as SignalType[]).filter(
            (signalType) => lifecycleStates[signalType] === "coming-soon",
        );
    }

    function getManagementSignalTypes(): readonly SignalType[] {
        return [...(salesSettingsHelper.isIntentSignalsEnabled() ? [signalTypeMap.intent] : []), ...validSignalTypes];
    }

    function getOrderedSignalTypes(): readonly SignalType[] {
        return [...getManagementSignalTypes(), ...getComingSoonSignalTypes()];
    }

    const iconNamePerSignalType: SignalTypeIcons = {
        [signalTypeMap.adNetwork]: "ad",
        [signalTypeMap.technology]: "computer-settings",
        [signalTypeMap.traffic]: "overview",
        [signalTypeMap.ecommerce]: "showcase-shopping",
        [signalTypeMap.news]: "serp-news",
        [signalTypeMap.intent]: "fire-outline",
        [signalTypeMap.advertiserActivity]: "ad-research",
        [signalTypeMap.apps]: "sidebar3-search-apps",
        [signalTypeMap.roleChanges]: "employee-growth",
    };
    const comingSoonUISignalFactory: UISignalFactory<SignalFilters> = {
        createUISignal(deps) {
            return {
                ...deps.signal,
                name: { key: "si.signals.coming_soon.name" },
                nameSegments: { signalType: { key: "si.signals.coming_soon.type" } },
                numberOfAccounts: 0,
                template: {
                    alias: "coming-soon",
                    description: { key: "si.signals.coming_soon.description" },
                    name: { key: "si.signals.coming_soon.name" },
                },
                signalSubType: "unknown",
            };
        },
    };
    const uiSignalFactoryBySignalType: Record<SignalType, UISignalFactory<SignalFilters>> = {
        [signalTypeMap.adNetwork]: adNetworkUISignalFactory,
        [signalTypeMap.intent]: intentUISignalFactory,
        [signalTypeMap.news]: newsUISignalFactory,
        [signalTypeMap.technology]: technologyUISignalFactory,
        [signalTypeMap.traffic]: trafficUISignalFactory,
        [signalTypeMap.ecommerce]: ecommerceUISignalFactory,
        [signalTypeMap.advertiserActivity]: comingSoonUISignalFactory,
        [signalTypeMap.apps]: comingSoonUISignalFactory,
        [signalTypeMap.roleChanges]: comingSoonUISignalFactory,
    };

    function getNewsCategories() {
        return NEWS_CATEGORIES_BY_TYPE;
    }

    function getUserCountries(): readonly ICountryObject[] {
        try {
            return settingsService.getCurrentComponentCountries();
        } catch (e) {
            console.error(e);

            return [];
        }
    }

    function getTemplatesManagersBySignalType(): Record<SignalType, SignalTemplatesManager<SignalFilters>> {
        const countries = getUserCountries();
        const newsCategories = getNewsCategories();

        return {
            [signalTypeMap.adNetwork]: new AdNetworkSignalTemplatesManager(countries),
            [signalTypeMap.technology]: new TechnologySignalTemplatesManager(countries),
            [signalTypeMap.traffic]: new TrafficSignalTemplatesManager(countries),
            [signalTypeMap.ecommerce]: new EcommerceSignalTemplatesManager(countries),
            [signalTypeMap.news]: new NewsSignalTemplatesManager(countries, newsCategories),
            [signalTypeMap.intent]: new IntentSignalTemplatesManager(countries),
            [signalTypeMap.advertiserActivity]: new ComingSoonSignalTemplatesManager(countries),
            [signalTypeMap.apps]: new ComingSoonSignalTemplatesManager(countries),
            [signalTypeMap.roleChanges]: new ComingSoonSignalTemplatesManager(countries),
        };
    }

    function getSignalCountryById(countryId: number | undefined): Readonly<ICountryObject> {
        if (is.undefined(countryId)) {
            throw new Error(`Provided 'countryId' is undefined`);
        }

        const userCountries = getUserCountries();

        const country = userCountries.find(({ id }) => id === countryId);

        if (typeof country === "undefined") {
            throw new Error(`Unable to find country available for user by ID: ${countryId}`);
        }

        return country;
    }

    function isTemplate(
        templateOrSignal: Signal | SignalTemplate<SignalFilters>,
    ): templateOrSignal is SignalTemplate<SignalFilters> {
        return typeof (templateOrSignal as Signal).id === "undefined";
    }

    function getEmptyCollectionsGroupedBySignalType<R>(): Record<SignalType, R[]> {
        return {
            [signalTypeMap.adNetwork]: [],
            [signalTypeMap.technology]: [],
            [signalTypeMap.traffic]: [],
            [signalTypeMap.ecommerce]: [],
            [signalTypeMap.news]: [],
            [signalTypeMap.intent]: [],
            [signalTypeMap.advertiserActivity]: [],
            [signalTypeMap.apps]: [],
            [signalTypeMap.roleChanges]: [],
        };
    }

    function getEmptySignalsSearchFilters(): SignalsSearchFilters {
        return {
            signals: [],
            numberOfAllSignals: 0,
            isNoSignalApplied: false,
            areSignalsAvailable: true,
        };
    }

    function getIconForSignalType(signalType: SignalType) {
        return iconNamePerSignalType[signalType];
    }

    const getPermanentSignals = <F extends SignalFilters = SignalFilters>(
        signals: readonly Signal<F>[],
    ): readonly IPermanentSignal<F>[] => {
        return signals.filter(isPermanentSignal);
    };

    function isSignalExpired<F extends SignalFilters = SignalFilters>(
        signal: Signal<F>,
        referenceDate: Date = new Date(),
    ): boolean {
        if (!isDraftSignal(signal)) {
            return false;
        }

        const expirationDate = new Date(signal.expiresAt);

        if (isNaN(expirationDate.getTime())) {
            Sentry.errorTracker({
                e: new Error("Invalid expiration date for draft signal."),
                level: "error",
                params: {
                    extra: {
                        signal,
                    },
                },
            });

            return true;
        }

        return expirationDate < referenceDate;
    }

    function getExpiredSignalIds<F extends SignalFilters>(
        signals: readonly Signal<F>[],
        referenceDate: Date = new Date(),
    ): readonly SignalId[] {
        return signals.filter((signal) => isSignalExpired(signal, referenceDate)).map((signal) => signal.id);
    }

    function getAllTemplatesGroupedBySignalType(): TemplatesBySignalType {
        const signalTypes = Object.values(signalTypeMap) as readonly SignalType[];
        const initialGroup = getEmptyCollectionsGroupedBySignalType() as TemplatesBySignalType;

        return signalTypes.reduce((group, signalType) => {
            const templatesManager = getTemplatesManagersBySignalType()[signalType];

            group[signalType] = templatesManager.getTemplates();

            return group;
        }, initialGroup);
    }

    function getUISignalFactory<F extends SignalFilters>(signal: Readonly<Signal<F>>): UISignalFactory<F> | undefined {
        return uiSignalFactoryBySignalType[signal.signalType] as UISignalFactory<F>;
    }

    /**
     * @throws TypeError
     */
    function getSignalTemplate<F extends SignalFilters>(signal: Readonly<Signal<F>>): SignalTemplate<F> | undefined {
        return getTemplatesManagersBySignalType()[signal.signalType].matchSignal(signal);
    }

    function getUISignalMapper(countPerSignalId: Readonly<CountPerSignalId>, mapperDeps: Readonly<UISignalDataDeps>) {
        return function toUISignal<F extends SignalFilters>(signal: Readonly<Signal<F>>): UISignal<F> {
            const numberOfAccounts = countPerSignalId[signal.id] ?? 0;

            try {
                const template = getSignalTemplate(signal) as SignalTemplate<F>;
                const uiSignalFactory = getUISignalFactory(signal) as UISignalFactory<F>;

                return uiSignalFactory.createUISignal({
                    ...mapperDeps,
                    numberOfAccounts,
                    template,
                    signal,
                });
            } catch (e) {
                Sentry.error("Couldn't create a UISignal:", e as Error);

                return {
                    ...signal,
                    name: {
                        key: "Unknown signal",
                    },
                    nameSegments: {
                        signalType: {
                            key: "Unknown signal",
                        },
                    },
                    numberOfAccounts,
                    template: {
                        alias: "unknown-signal",
                        description: { key: "Unknown signal" },
                        name: { key: "Unknown signal" },
                    },
                    signalSubType: "unknown",
                };
            }
        };
    }

    function getSignalDefaultTemplate(signalType: SignalType): SignalTemplate<SignalFilters> {
        const templatesManager = getTemplatesManagersBySignalType()[signalType];

        return templatesManager.getDefaultTemplate();
    }

    function sortByCreatedDateDesc<S extends Signal>(signals: readonly S[]): S[] {
        return signals.slice().sort((a, b) => {
            const timeA = new Date(a.createdAt).getTime();
            const timeB = new Date(b.createdAt).getTime();

            return timeA - timeB;
        });
    }

    function sortUISignalsGroupedByType(groupedSignals: UISignalsGroupedByType) {
        return objectKeys(groupedSignals).reduce((group, type) => {
            group[type] = sortByCreatedDateDesc(groupedSignals[type]);

            return group;
        }, {} as UISignalsGroupedByType);
    }

    function createSignalsDTOsGroupedByType(signals: readonly Signal[]): SignalsGroupedByType {
        const emptyGroup = getEmptyCollectionsGroupedBySignalType<Signal>();

        return signals.reduce((group, signal) => {
            if (getSignalTypeLifecycleStates()[signal.signalType] === "coming-soon") {
                return group;
            }

            group[signal.signalType]?.push(signal);

            return group;
        }, emptyGroup);
    }

    function createUISignalsGroupedById(
        signalIds: readonly SignalId[],
        signalsGroupedById: Readonly<SignalsGroupedById>,
        countPerSignalId: Readonly<CountPerSignalId>,
        signalsDescription: Readonly<SignalsDescription> | null,
        signalsTechnologies: GroupedTechnologies,
        intentSelectedTopics: Topic[],
        intentUserCountries: readonly ICountryObject[],
    ) {
        const toUISignal = getUISignalMapper(countPerSignalId, {
            adNetworks: signalsDescription?.adNetworks ?? [],
            countries: getUserCountries(),
            technologies: signalsTechnologies,
            technologiesDate: signalsDescription?.technologiesDate ?? new Date(),
            newsCategories: getNewsCategories(),
            intentSignalSelectedTopics: intentSelectedTopics,
            intentUserCountries: intentUserCountries,
        });
        const appliedUISignalsGroupedById = {} as Record<SignalId, UISignal<SignalFilters>>;

        return signalIds.reduce((group, signalId) => {
            const signal = signalsGroupedById[signalId];

            group[signalId] = toUISignal(signal);

            return group;
        }, appliedUISignalsGroupedById);
    }

    function createUISignalsGroupedByType(
        signalsGroupedById: Readonly<SignalsGroupedById>,
        countPerSignalId: Readonly<CountPerSignalId>,
        signalsDescription: Readonly<SignalsDescription>,
        signalsTechnologies: GroupedTechnologies,
        intentSelectedTopics: Topic[],
        intentUserCountries: readonly ICountryObject[],
    ): UISignalsGroupedByType {
        const initialGroup: Record<SignalType, UISignal<SignalFilters>[]> = getEmptyCollectionsGroupedBySignalType();
        const toUISignal = getUISignalMapper(countPerSignalId, {
            adNetworks: signalsDescription?.adNetworks ?? [],
            countries: getUserCountries(),
            technologies: signalsTechnologies,
            technologiesDate: signalsDescription?.technologiesDate,
            newsCategories: getNewsCategories(),
            intentUserCountries: intentUserCountries ?? [],
            intentSignalSelectedTopics: intentSelectedTopics ?? [],
        });

        return sortUISignalsGroupedByType(
            Object.keys(signalsGroupedById).reduce((group, signalId) => {
                const signal = signalsGroupedById[signalId];

                group[signal.signalType]?.push(toUISignal(signal));

                return group;
            }, initialGroup),
        );
    }

    function getDefaultSignalsDTOs(): SignalCreateDTO<SignalFilters>[] {
        const newsSignalTemplatesManager = getTemplatesManagersBySignalType()[signalTypeMap.news];
        const trafficSignalsTemplatesManager = getTemplatesManagersBySignalType()[signalTypeMap.traffic];
        const newsSignalsTemplates = newsSignalTemplatesManager.getTemplates();
        const trafficSignalsTemplates = trafficSignalsTemplatesManager.getTemplates();

        const recommendedSignalTemplates = [
            ...newsSignalsTemplates.filter((t) => {
                return [
                    NewsSignalSubType.Leadership,
                    NewsSignalSubType.Growth,
                    NewsSignalSubType.Strategy,
                    NewsSignalSubType.Investment,
                    NewsSignalSubType.CompanyChallenges,
                ].includes(t.signalSubType as NewsSignalSubType);
            }),
            ...trafficSignalsTemplates.filter((t) => {
                return [TrafficSignalSubType.GettingTraction, TrafficSignalSubType.LosingTraction].includes(
                    t.signalSubType as TrafficSignalSubType,
                );
            }),
        ];

        return recommendedSignalTemplates.map(
            ({ signalType, signalSubType, filters, signalSource, lifecycleStatus }) => {
                return { isDefault: true, signalType, signalSubType, filters, signalSource, lifecycleStatus };
            },
        );
    }

    function getSignalFromUISignal<F extends SignalFilters>(uiSignal: UISignal<F>): Signal<F> {
        const {
            createdAt,
            filters,
            id,
            signalType,
            signalSubType,
            updatedAt,
            isDefault,
            signalSource,
            lifecycleStatus,
        } = uiSignal;

        const baseSignal = {
            id,
            lifecycleStatus,
            createdAt,
            updatedAt,
            isDefault,
            signalType,
            signalSource,
            signalSubType,
            filters,
        };

        if (isDraftSignal(uiSignal)) {
            return {
                ...baseSignal,
                expiresAt: uiSignal.expiresAt,
            };
        }

        return baseSignal as IPermanentSignal;
    }

    function getSignalCreateDTO<F extends SignalFilters>(
        template: Readonly<SignalTemplate<F>>,
        filters: Readonly<F>,
        signalsSource: SignalSource,
    ): Readonly<SignalCreateDTO<F>> {
        return template.getSignalCreateDTO(filters, signalsSource);
    }

    function getSignalUpdateDTO<F extends SignalFilters>(
        uiSignal: Readonly<UISignal<F>>,
        filters: Readonly<F>,
    ): Readonly<SignalUpdateDTO<F>> {
        const { id, isDefault, signalType, signalSubType } = uiSignal;
        const signal = getSignalFromUISignal(uiSignal);
        const template = getSignalTemplate(signal);

        if (typeof template !== "undefined") {
            return template.getSignalUpdateDTO(uiSignal, filters);
        }

        return { id, isDefault, signalType, signalSubType, filters };
    }

    function groupSignalsById(signals: readonly Signal[]): SignalsGroupedById {
        const initialGroup: SignalsGroupedById = {};

        return signals.reduce((group, signal) => {
            group[signal.id] = signal;

            return group;
        }, initialGroup);
    }

    function groupSignalsByType(signalsById: Readonly<SignalsGroupedById>): SignalsGroupedByType {
        const initialGroup: Record<SignalType, Signal[]> = getEmptyCollectionsGroupedBySignalType();

        return Object.keys(signalsById).reduce((group, signalId) => {
            const signal = signalsById[signalId];

            if (Array.isArray(group[signal.signalType])) {
                group[signal.signalType].push(signal);
            }

            return group;
        }, initialGroup);
    }

    function groupUISignalsByType(uiSignals: readonly UISignal<SignalFilters>[]): UISignalsGroupedByType {
        const initialGroup: Record<SignalType, UISignal<SignalFilters>[]> = getEmptyCollectionsGroupedBySignalType();

        return uiSignals.reduce((group, signal) => {
            if (Array.isArray(group[signal.signalType])) {
                group[signal.signalType].push(signal);
            }

            return group;
        }, initialGroup) as UISignalsGroupedByType;
    }

    function signalsDigestTypeToPeriod(digestType: SignalsDigestType) {
        return {
            Daily: SignalTimeframePeriod.Day,
            Weekly: SignalTimeframePeriod.Week,
            Monthly: SignalTimeframePeriod.Month,
            MonthlyAndWeekly: SignalTimeframePeriod.Month,
        }[digestType];
    }

    function signalsDigestTypeToTimeGranularity(digestType: SignalsDigestType): OpUnitType {
        if (digestType === "MonthlyAndWeekly") {
            return granularityNameToMomentUnit.Monthly;
        }

        return granularityNameToMomentUnit[digestType];
    }

    function getPreAppliedSignalsBasedOnParams(
        params: Record<string, string>,
        signalsGroupedById: Readonly<SignalsGroupedById>,
    ): PreAppliedSignalsPayload {
        const emptyPayload: PreAppliedSignalsPayload = {
            signalsGroupedById,
            haveSignalsToApply: false,
            signalsIdsToApply: [],
        };
        const { digestType, signalId } = params;

        const normalizedSignalId = Array.isArray(signalId) ? signalId[0] : signalId;

        if (normalizedSignalId === "any") {
            const allSignalsIds = Object.keys(signalsGroupedById);

            return {
                signalsGroupedById,
                haveSignalsToApply: true,
                signalsIdsToApply: allSignalsIds,
            };
        }

        const foundSignal = signalsGroupedById[normalizedSignalId];

        if (typeof foundSignal !== "undefined") {
            if (
                typeof digestType !== "undefined" &&
                typeof (
                    foundSignal.filters as SignalFilters & {
                        period: SignalTimeframePeriod;
                    }
                ).period !== "undefined"
            ) {
                const timeframePeriod = signalsDigestTypeToPeriod(digestType as SignalsDigestType);

                if (typeof timeframePeriod !== "undefined") {
                    return {
                        haveSignalsToApply: true,
                        signalsIdsToApply: [foundSignal.id],
                        signalsGroupedById: {
                            ...signalsGroupedById,
                            [foundSignal.id]: {
                                ...foundSignal,
                                filters: {
                                    ...foundSignal.filters,
                                    period: timeframePeriod,
                                },
                            } as Signal,
                        },
                    };
                }
            }

            return {
                signalsGroupedById,
                haveSignalsToApply: true,
                signalsIdsToApply: [foundSignal.id],
            };
        }

        return emptyPayload;
    }

    function isSignalsDigestExpired(digestParams: SignalsDigestParams): boolean {
        const { digestDate, digestType } = digestParams;

        if (typeof digestDate === "undefined" || typeof digestType === "undefined") {
            return false;
        }

        const currentDate = dayjs();
        const digestTimeGranularity = signalsDigestTypeToTimeGranularity(digestType);
        const digestExpirationDate = dayjs(digestDate).add(1, digestTimeGranularity);

        return currentDate.isAfter(digestExpirationDate);
    }

    function mapSignalsNamesTranslationToIds(uiSignals: UISignalsGroupedByType) {
        return Object.values(uiSignals).reduce<Record<SignalId, string>>((acc, uiSignalsByType) => {
            if (uiSignalsByType.length > 0) {
                uiSignalsByType.forEach((signal) => {
                    acc[signal.id] = translateNested(signal.name);
                });
            }

            return acc;
        }, {});
    }

    function filterSignalsBySource<S extends Signal>(
        signals: Readonly<SignalsGroupedById<S>>,
        signalSource: SignalSource,
    ): SignalsGroupedById<S> {
        return objectKeys(signals).reduce((acc, signalId) => {
            if (signals[signalId].signalSource === signalSource) {
                acc[signalId] = signals[signalId];
            }

            return acc;
        }, {} as SignalsGroupedById<S>);
    }

    function deduplicateAdminSignals(signalsById: Readonly<SignalsGroupedById>): SignalsGroupedById {
        const allSignals = Object.values(signalsById);

        const userSignals: Signal[] = [];
        const adminPushedSignals: Signal[] = [];

        for (const signal of allSignals) {
            if (signal.accountSignalId) {
                adminPushedSignals.push(signal);
            } else {
                userSignals.push(signal);
            }
        }

        if (adminPushedSignals.length === 0) {
            return signalsById;
        }

        const userSignalsByTypeKey = new Map<string, Signal[]>();
        for (const signal of userSignals) {
            const key = `${signal.signalSource}:${signal.signalType}:${signal.signalSubType ?? ""}`;
            const list = userSignalsByTypeKey.get(key) ?? [];
            list.push(signal);
            userSignalsByTypeKey.set(key, list);
        }

        const duplicateIds = new Set<SignalId>();

        for (const adminSignal of adminPushedSignals) {
            const key = `${adminSignal.signalSource}:${adminSignal.signalType}:${adminSignal.signalSubType ?? ""}`;
            const candidates = userSignalsByTypeKey.get(key);

            if (!candidates) {
                continue;
            }

            const predicate = filterService.getFiltersEqualityPredicate(adminSignal.signalType);

            if (candidates.some((c) => predicate(c.filters, adminSignal.filters))) {
                duplicateIds.add(adminSignal.id);
            }
        }

        if (duplicateIds.size === 0) {
            return signalsById;
        }

        return objectKeys(signalsById).reduce((acc, id) => {
            if (!duplicateIds.has(id)) {
                acc[id] = signalsById[id];
            }

            return acc;
        }, {} as SignalsGroupedById);
    }

    function sanitizeSignalName(name: string): string {
        return globalStringSanitizePipeline.apply(name);
    }

    function areAllSignalsDefault({
        numberOfAllSignals,
        signalsGroupedById,
    }: {
        numberOfAllSignals: number;
        signalsGroupedById: Readonly<SignalsGroupedById>;
    }) {
        const defaultSignals = getDefaultSignalsDTOs();

        if (numberOfAllSignals !== defaultSignals.length) {
            return false;
        }

        return !Object.values(signalsGroupedById).some(({ isDefault }) => !isDefault);
    }

    function removeSignalsByType<S extends Signal = Signal>(
        signalsIds: readonly SignalId[],
        signalTypesToRemove: readonly SignalType[],
        signalsGroupedById: Readonly<SignalsGroupedById<S>>,
    ): readonly SignalId[] {
        const typesSet = new Set(signalTypesToRemove);

        return signalsIds.filter((id) => {
            const signal = signalsGroupedById[id as S["id"]];

            return signal && !typesSet.has(signal.signalType);
        });
    }

    function appendUniqueSignalsToAppliedSignals<S extends Signal = Signal>(
        appliedSignalsIds: readonly SignalId[],
        signalIdsToAdd: readonly SignalId[],
        signalsGroupedById: Readonly<SignalsGroupedById<S>>,
    ): readonly SignalId[] {
        const signalsToAdd = signalIdsToAdd.filter((id) => signalsGroupedById[id as S["id"]]);

        if (signalsToAdd.length === 0) {
            return appliedSignalsIds;
        }

        const existingIds = new Set(appliedSignalsIds);
        const newIds = signalsToAdd.filter((id) => !existingIds.has(id));

        return [...appliedSignalsIds, ...newIds];
    }

    function getSignalsByIds<S extends Signal = Signal>(
        signalsIds: readonly SignalId[],
        signalsGroupedById: Readonly<SignalsGroupedById<S>>,
    ): readonly S[] {
        return signalsIds
            .map((id) => signalsGroupedById[id as S["id"]])
            .filter((signal): signal is S => signal !== undefined);
    }

    const getSignalTypeLifecycleState = (signalType: SignalType): SignalLifecycleState | undefined => {
        const explicit = getSignalTypeLifecycleStates()[signalType];

        if (explicit) {
            return explicit;
        }

        const templates = getTemplatesManagersBySignalType()[signalType].getTemplates();

        if (templates.some((t) => t.signalLifecycleState === "updated")) {
            return "updated";
        }

        if (templates.some((t) => t.signalLifecycleState === "post-beta")) {
            return "post-beta";
        }

        return;
    };

    return {
        get orderedSignalTypes(): readonly SignalType[] {
            return getOrderedSignalTypes();
        },
        get managementSignalTypes(): readonly SignalType[] {
            return getManagementSignalTypes();
        },
        getSignalTypeLifecycleState,
        createSignalsDTOsGroupedByType,
        createUISignalsGroupedById,
        createUISignalsGroupedByType,
        getPermanentSignals,
        getExpiredSignalIds,
        getAllTemplatesGroupedBySignalType,
        getEmptyCollectionsGroupedBySignalType,
        getIconForSignalType,
        getPreAppliedSignalsBasedOnParams,
        getDefaultSignalsDTOs,
        getSignalCreateDTO,
        getSignalUpdateDTO,
        getSignalFromUISignal,
        getSignalDefaultTemplate,
        getSignalCountryById,
        groupSignalsById,
        groupSignalsByType,
        groupUISignalsByType,
        getSignalTemplate,
        isTemplate,
        isSignalExpired,
        isSignalsDigestExpired,
        areAllSignalsDefault,
        getEmptySignalsSearchFilters,
        mapSignalsNamesTranslationToIds,
        appendUniqueSignalsToAppliedSignals,
        removeSignalsByType,
        getSignalsByIds,
        filterSignalsBySource,
        deduplicateAdminSignals,
        getSignalIdsFromGroupedSignals,
        sanitizeSignalName,
    };
}
